<template>
  <article
    class="overflow-hidden rounded-2xl border bg-[rgba(1,12,30,0.78)] shadow-lg shadow-black/25 transition-colors"
    :class="pickedSide ? '' : 'border-white/10'"
    :style="cardStyle"
  >
    <!-- One content band. Everything that describes the game and its verdict
         lives here; the only rule on the card separates content from chrome. -->
    <section class="px-4 pt-4 pb-3.5 sm:px-5">
      <div class="mb-3 flex items-center justify-between gap-3">
        <p class="text-sm font-medium text-slate-300">{{ kickoff }}</p>
        <NetworkBadge :broadcast="game.broadcast" :week="game.week" />
      </div>

      <!-- "away @ home", the way a schedule prints it, level with the logos:
           it is what says which side is home. -->
      <div class="relative grid grid-cols-2 gap-6">
        <span class="pointer-events-none absolute left-1/2 top-5 -translate-x-1/2 -translate-y-1/2 text-base font-bold text-slate-400" aria-hidden="true">@</span>
        <TeamSide
          :team="game.awayTeam"
          :is-home="false"
          :frozen-spread="game.fivePicksSpread"
          :pickable="pickable"
          :picked="pickedSide === 'away'"
          @pick="$emit('pick', 'away')"
        />
        <TeamSide
          :team="game.homeTeam"
          :is-home="true"
          :frozen-spread="game.fivePicksSpread"
          :pickable="pickable"
          :picked="pickedSide === 'home'"
          @pick="$emit('pick', 'home')"
        />
      </div>

      <EdgeVerdict :game="game" layout="card" class="mt-3.5" />
      <MorningCall :game="game" :edge="edge" class="mt-2" />
    </section>

    <!-- Chrome. One strip of toggles: quick peeks at the evidence that backs
         the verdict above, plus a link to the full detail page. -->
    <footer class="border-t border-white/10 bg-black/15">
      <div class="grid grid-cols-4 gap-1 px-2 py-1 sm:px-3">
        <button
          type="button"
          @click="togglePanel('history')"
          class="flex h-10 flex-row items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors sm:h-11 sm:text-sm"
          :class="open === 'history' ? openPanelClass : idlePanelClass"
          :aria-expanded="open === 'history'"
        >
          <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Last 5</span>
        </button>

        <button
          type="button"
          @click="togglePanel('books')"
          class="flex h-10 flex-row items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors sm:h-11 sm:text-sm"
          :class="open === 'books' ? openPanelClass : idlePanelClass"
          :aria-expanded="open === 'books'"
        >
          <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
          </svg>
          <span class="whitespace-nowrap tabular-nums">{{ game.spreads.length }} Books</span>
        </button>

        <button
          type="button"
          @click="togglePanel('injuries')"
          class="flex h-10 flex-row items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors sm:h-11 sm:text-sm"
          :class="open === 'injuries' ? openPanelClass : idlePanelClass"
          :aria-expanded="open === 'injuries'"
        >
          <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <span>Injuries</span>
        </button>

        <router-link
          :to="`/games/${game.id}`"
          class="flex h-10 flex-row items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors sm:h-11 sm:text-sm"
          :class="idlePanelClass"
        >
          <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
          </svg>
          <span>Insights</span>
        </router-link>
      </div>

      <div v-show="open === 'books'" class="border-t border-white/10 bg-brand-bg/45 px-3 py-3.5 sm:px-4">
        <BookmakerTable :game="game" />
      </div>

      <div v-show="open === 'history'" class="border-t border-white/10 bg-brand-bg/45 p-3 sm:p-4">
        <PastGames :game="game" :limit="5" />
      </div>

      <div v-show="open === 'injuries'" class="border-t border-white/10 bg-brand-bg/45 p-3 sm:p-4">
        <InjuryReport :game="game" :limit="3" />
      </div>
    </footer>
  </article>
</template>

<script>
import TeamSide from "./TeamSide.vue";
import EdgeVerdict from "./EdgeVerdict.vue";
import BookmakerTable from "./BookmakerTable.vue";
import PastGames from "./PastGames.vue";
import InjuryReport from "./InjuryReport.vue";
import NetworkBadge from "./NetworkBadge.vue";
import MorningCall from "./MorningCall.vue";
import { computeEdge } from "../utils/edge.js";
import { teamAccent } from "../utils/teamColors.js";

export default {
  name: "GameCard",
  components: { TeamSide, EdgeVerdict, BookmakerTable, PastGames, InjuryReport, NetworkBadge, MorningCall },
  props: {
    game: { type: Object, required: true },
    // Turns each TeamSide into a click-to-pick control.
    pickable: { type: Boolean, default: false },
    // 'home' | 'away' | null -- which side is currently picked for this game.
    pickedSide: { type: String, default: null },
    // Set for a moment after arriving here via Picks' "view on Home" link,
    // so the game you tapped is unmistakable in a list sorted by edge, not
    // by the order you last saw it in.
    highlighted: { type: Boolean, default: false },
  },
  emits: ["pick"],
  data() {
    return {
      // 'books' | 'history' | 'injuries' | null -- one panel open at a time,
      // since the footer is a single row of toggles.
      open: null,
    };
  },
  computed: {
    edge() {
      return computeEdge(this.game);
    },
    // The picked side's own colour becomes the whole card's border/background,
    // rather than a badge inside one row -- easy to spot scanning a long list.
    pickedTeam() {
      if (this.pickedSide === "home") return this.game.homeTeam;
      if (this.pickedSide === "away") return this.game.awayTeam;
      return null;
    },
    awayAccent() {
      return teamAccent(this.game.awayTeam.name || this.game.awayTeam.abbr);
    },
    homeAccent() {
      return teamAccent(this.game.homeTeam.name || this.game.homeTeam.abbr);
    },
    cardStyle() {
      const style = {};
      // Each side of the card in its own team's colour, strongest at the edge
      // and gone by the middle, so the two halves above read as the two teams.
      const a = this.awayAccent;
      const h = this.homeAccent;
      style.backgroundImage = `linear-gradient(90deg, ${a}42 0%, ${a}0d 40%, transparent 50%, ${h}0d 60%, ${h}42 100%)`;

      if (this.pickedTeam) {
        const c = teamAccent(this.pickedTeam.name || this.pickedTeam.abbr);
        style.borderColor = `${c}aa`;
        style.boxShadow = `0 0 0 1px ${c}55 inset`;
      }
      // Layered on top rather than swapped in -- the pick's own colour, if
      // any, should still show once the highlight fades a few seconds later.
      if (this.highlighted) {
        style.borderColor = "#34d399";
        style.boxShadow = [
          style.boxShadow,
          "0 0 0 3px rgba(52,211,153,0.45)",
          "0 0 24px rgba(52,211,153,0.35)",
        ]
          .filter(Boolean)
          .join(", ");
      }
      return style;
    },
    // "Sun 1:00 PM - 11/23" is what the API hands us; the dash reads as a
    // minus sign on a page covered in negative spreads.
    kickoff() {
      const [when = "", date = ""] = String(this.game.gameDate).split(" - ");
      return date ? `${when} · ${date}` : when;
    },
    openPanelClass() {
      // White, like the pick button: green on the card means line value.
      return "bg-white/10 text-white";
    },
    idlePanelClass() {
      return "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200";
    },
  },
  methods: {
    togglePanel(panel) {
      this.open = this.open === panel ? null : panel;
    },
  },
};
</script>
